// Andon 실시간 보드 — 우선순위 색 코딩 + ack/resolve 워크플로우 + 15초 폴링
import { useCallback, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Card } from '../components/Card'
import { Button } from '../components/Button'
import { Counter } from '../components/Counter'
import { Skeleton } from '../components/Skeleton'
import { api } from '../lib/api'
import { useToast } from '../components/Toast'
import { useGlobalReveal } from '../hooks/useReveal'
import { useAuth, hasPerm } from '../lib/auth'
import { useT } from '../lib/i18n'

type Severity = 'critical' | 'high' | 'medium' | 'low'
type Category = 'machine' | 'quality' | 'material' | 'safety' | 'support'

interface OpenEvent {
  event_id: string
  equipment_id: string
  equipment_name?: string
  category: Category
  severity: Severity
  message: string
  raised_at: string
  acknowledged_at: string | null
  acknowledged_by: string | null
}

interface RecentEvent extends OpenEvent {
  resolved_at: string
  resolved_by: string | null
  resolution_note: string | null
}

interface BoardResp {
  open: OpenEvent[]
  recent: RecentEvent[]
  kpi: { open_count: number; mttr_minutes: number | null; resolved_7d: number }
}

const SEV_COLOR: Record<Severity, string> = {
  critical: 'danger',
  high: 'warn',
  medium: 'info',
  low: '',
}
const SEV_BORDER: Record<Severity, string> = {
  critical: 'var(--color-danger)',
  high: 'var(--color-warning)',
  medium: 'var(--color-accent)',
  low: 'var(--color-line-strong)',
}

function elapsed(ts: string): string {
  const ms = Date.now() - new Date(ts).getTime()
  const m = Math.floor(ms / 60000)
  if (m < 1) return '방금'
  if (m < 60) return `${m}분 전`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}시간 ${m % 60}분 전`
  return `${Math.floor(h / 24)}일 전`
}

export function AndonPage() {
  useGlobalReveal()
  const toast = useToast()
  const { t } = useT()
  const { user } = useAuth()
  const canWrite = hasPerm(user, 'andon', 'write')

  const [board, setBoard] = useState<BoardResp | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [resolveNote, setResolveNote] = useState<{ id: string; note: string } | null>(null)
  const timerRef = useRef<number | null>(null)

  const load = useCallback(async () => {
    try {
      setBoard(await api.get<BoardResp>('/andon'))
    } catch (e: any) {
      toast.push('error', e?.message ?? t('toast.fetchFailed'))
    }
  }, [toast, t])

  useEffect(() => {
    load()
    timerRef.current = window.setInterval(load, 15000)
    return () => { if (timerRef.current) window.clearInterval(timerRef.current) }
  }, [load])

  const ack = async (id: string) => {
    setBusy(id)
    try {
      await api.post(`/andon/${id}/ack`)
      toast.push('success', t('andon.acked'))
      load()
    } catch (e: any) { toast.push('error', e?.message ?? t('toast.actionFailed')) }
    finally { setBusy(null) }
  }

  const resolve = async () => {
    if (!resolveNote) return
    setBusy(resolveNote.id)
    try {
      await api.post(`/andon/${resolveNote.id}/resolve`, { resolution_note: resolveNote.note })
      toast.push('success', t('andon.resolved'))
      setResolveNote(null)
      load()
    } catch (e: any) { toast.push('error', e?.message ?? t('toast.actionFailed')) }
    finally { setBusy(null) }
  }

  return (
    <main id="main" className="container stack" style={{ paddingBlock: '2rem' }}>
      <div className="row space-between reveal">
        <div>
          <h1 className="page-title" style={{ marginBottom: '0.2rem' }}>
            <FontAwesomeIcon icon="tower-observation" /> {t('andon.title')}
          </h1>
          <p className="muted" style={{ marginBottom: 0 }}>
            {t('andon.intro')}
          </p>
        </div>
        <span className="badge"><FontAwesomeIcon icon="clock" /> {t('andon.autoRefresh')}</span>
      </div>

      {/* KPI 카드 */}
      <section
        className="reveal"
        style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}
      >
        <Card glass tilt={false} spotlight={false}>
          <span className="badge danger"><FontAwesomeIcon icon="triangle-exclamation" /> 진행 중</span>
          <p className="mega-num" style={{ margin: '0.4rem 0 0' }}>
            {board ? <Counter value={board.kpi.open_count} suffix=" 건" /> : <Skeleton h={56} />}
          </p>
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge info"><FontAwesomeIcon icon="clock" /> 평균 해소 시간 (7일)</span>
          <p className="kpi-num">
            {board ? (board.kpi.mttr_minutes ?? '—') + (board.kpi.mttr_minutes ? '분' : '') : <Skeleton h={36} />}
          </p>
        </Card>
        <Card glass tilt={false} spotlight={false}>
          <span className="badge success"><FontAwesomeIcon icon="user-check" /> 해소 (7일)</span>
          <p className="kpi-num">
            {board ? <Counter value={board.kpi.resolved_7d} suffix=" 건" /> : <Skeleton h={36} />}
          </p>
        </Card>
      </section>

      {/* 진행 중 카드들 (severity 색 코딩) */}
      <section aria-labelledby="open-h" className="reveal">
        <h2 id="open-h" style={{ fontSize: 'var(--fs-lg)' }}>진행 중 알람</h2>
        {!board ? (
          <Card glass tilt={false} spotlight={false}><Skeleton h={120} /></Card>
        ) : board.open.length === 0 ? (
          <Card glass tilt={false} spotlight={false}>
            <div className="empty-state" role="status">
              <FontAwesomeIcon icon="circle-check" className="icon-lg" style={{ color: 'var(--color-success)' }} />
              <p className="muted" style={{ margin: 0 }}>현재 진행 중인 알람이 없습니다.</p>
            </div>
          </Card>
        ) : (
          <div style={{ display: 'grid', gap: '1rem', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
            {board.open.map((e) => (
              <Card
                key={e.event_id}
                glass
                tilt={false}
                spotlight={false}
                className={e.severity === 'critical' ? 'pulse-critical' : undefined}
                style={{ borderLeft: `4px solid ${SEV_BORDER[e.severity]}` }}
              >
                <div className="row" style={{ marginBottom: '0.5rem' }}>
                  <span className={`badge ${SEV_COLOR[e.severity]}`}>{e.severity.toUpperCase()}</span>
                  <span className="badge">{e.category}</span>
                  <span className="muted" style={{ marginLeft: 'auto', fontSize: 'var(--fs-xs)' }}>{elapsed(e.raised_at)}</span>
                </div>
                <h3 style={{ fontSize: 'var(--fs-lg)', margin: '0.2rem 0 0.4rem' }}>{e.message}</h3>
                <p className="mono muted" style={{ marginBottom: '0.8rem', fontSize: 'var(--fs-sm)' }}>
                  {e.equipment_id} · {e.equipment_name ?? ''}
                </p>
                {e.acknowledged_at && (
                  <p className="muted" style={{ fontSize: 'var(--fs-sm)', marginBottom: '0.6rem' }}>
                    <FontAwesomeIcon icon="user-check" /> 인지 by {e.acknowledged_by} · {elapsed(e.acknowledged_at)}
                  </p>
                )}
                <div className="row" style={{ gap: '0.5rem' }}>
                  {!e.acknowledged_at && (
                    <Button
                      variant="ghost"
                      magnetic={false}
                      disabled={!canWrite || busy === e.event_id}
                      onClick={() => ack(e.event_id)}
                      aria-label={`${e.event_id} 인지`}
                    >
                      <FontAwesomeIcon icon={busy === e.event_id ? 'circle-notch' : 'user-check'} spin={busy === e.event_id} />
                      <span>인지</span>
                    </Button>
                  )}
                  <Button
                    variant="accent"
                    magnetic={false}
                    disabled={!canWrite || busy === e.event_id}
                    onClick={() => setResolveNote({ id: e.event_id, note: '' })}
                    aria-label={`${e.event_id} 해소`}
                  >
                    <FontAwesomeIcon icon="check" />
                    <span>해소</span>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Recent timeline */}
      {board && board.recent.length > 0 && (
        <Card glass tilt={false} spotlight={false} className="reveal" as="section" aria-labelledby="rec-h">
          <h2 id="rec-h" style={{ fontSize: 'var(--fs-lg)' }}>최근 24시간 해소 이력</h2>
          <ul style={{ paddingLeft: 0, listStyle: 'none', display: 'grid', gap: '0.5rem' }}>
            {board.recent.map((r) => (
              <li key={r.event_id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '0.6rem', alignItems: 'center', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius-sm)', background: 'var(--color-bg-soft)' }}>
                <span className={`badge ${SEV_COLOR[r.severity]}`}>{r.severity}</span>
                <div>
                  <div>{r.message} <span className="muted">— {r.equipment_id}</span></div>
                  {r.resolution_note && (
                    <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
                      <FontAwesomeIcon icon="check" /> {r.resolution_note}
                    </div>
                  )}
                </div>
                <span className="muted mono" style={{ fontSize: 'var(--fs-sm)' }}>{elapsed(r.resolved_at)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Resolve 모달 */}
      {resolveNote && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="알람 해소"
          onClick={(e) => { if (e.target === e.currentTarget) setResolveNote(null) }}
          style={{
            position: 'fixed', inset: 0, zIndex: 70,
            background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
            display: 'grid', placeItems: 'center', padding: '1rem',
          }}
        >
          <Card glass tilt={false} spotlight={false} style={{ width: 'min(480px, 100%)' }}>
            <h2 style={{ fontSize: 'var(--fs-lg)' }}>알람 해소</h2>
            <div className="field">
              <label htmlFor="rnote">조치 내용 (필수)</label>
              <textarea
                id="rnote"
                rows={4}
                placeholder="예: 냉각수 보충 후 모터 정상화 확인"
                value={resolveNote.note}
                onChange={(e) => setResolveNote({ ...resolveNote, note: e.target.value })}
                style={{ width: '100%', padding: '0.65rem 0.85rem', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-line-strong)', background: 'var(--color-surface)' }}
              />
            </div>
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              <Button variant="ghost" magnetic={false} onClick={() => setResolveNote(null)}>취소</Button>
              <Button
                variant="accent"
                magnetic={false}
                disabled={!resolveNote.note.trim() || busy === resolveNote.id}
                onClick={resolve}
              >
                <FontAwesomeIcon icon={busy === resolveNote.id ? 'circle-notch' : 'check'} spin={busy === resolveNote.id} />
                <span>해소 처리</span>
              </Button>
            </div>
          </Card>
        </div>
      )}

      <style>{`
        @keyframes pulse-critical-anim {
          0%, 100% { box-shadow: var(--shadow-md); }
          50% { box-shadow: 0 0 0 4px color-mix(in oklab, var(--color-danger) 30%, transparent), var(--shadow-md); }
        }
        .pulse-critical { animation: pulse-critical-anim 2s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .pulse-critical { animation: none; } }
      `}</style>
    </main>
  )
}
